import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { toast } from 'sonner';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  ArrowLeft, Camera, Upload, Car, User, Phone, Mail,
  Loader2, Sparkles, ChevronRight, Mic, MicOff, Square, Check,
  AlertTriangle, Gauge, Fuel, Edit3, Plus, X, Save, CalendarDays, MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Voice-to-text hook
const useVoiceInput = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-IN'; // Indian English

      recognitionRef.current.onresult = (event) => {
        const current = event.resultIndex;
        const result = event.results[current];
        setTranscript(result[0].transcript);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please enable microphone.');
        }
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  const startListening = () => {
    if (recognitionRef.current) {
      setTranscript('');
      recognitionRef.current.start();
      setIsListening(true);
    } else {
      toast.error('Voice input not supported in this browser');
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  return { isListening, transcript, startListening, stopListening, setTranscript };
};

// Voice Input Button Component
const VoiceInputButton = ({ onResult, placeholder, className = '' }) => {
  const { isListening, transcript, startListening, stopListening, setTranscript } = useVoiceInput();

  useEffect(() => {
    if (transcript && !isListening) {
      onResult(transcript);
      setTranscript('');
    }
  }, [transcript, isListening, onResult, setTranscript]);

  return (
    <Button
      type="button"
      variant={isListening ? "destructive" : "outline"}
      size="icon"
      onClick={isListening ? stopListening : startListening}
      className={`flex-shrink-0 ${isListening ? 'animate-pulse bg-red-500' : 'border-orange-500 text-orange-500 hover:bg-orange-500/10'} ${className}`}
      title={isListening ? 'Stop recording' : placeholder || 'Click to speak'}
    >
      {isListening ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
    </Button>
  );
};

// Damage Marker Component - displays a marker on the image
const DamageMarker = ({ annotation, onClick, isSelected }) => {
  const severityColors = {
    minor: 'bg-yellow-500 border-yellow-300',
    moderate: 'bg-orange-500 border-orange-300',
    severe: 'bg-red-500 border-red-300'
  };
  
  const colorClass = severityColors[annotation.severity] || severityColors.moderate;
  
  return (
    <div
      className={`absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all z-10
        ${isSelected ? 'scale-125' : 'hover:scale-110'}`}
      style={{
        left: `${annotation.location_x}%`,
        top: `${annotation.location_y}%`
      }}
      onClick={() => onClick && onClick(annotation)}
      title={annotation.description}
    >
      {/* Pulsing ring effect */}
      <div className={`absolute inset-0 rounded-full ${colorClass} animate-ping opacity-50`} 
           style={{ width: '24px', height: '24px', marginLeft: '-4px', marginTop: '-4px' }} />
      {/* Main marker */}
      <div className={`w-4 h-4 rounded-full ${colorClass} border-2 flex items-center justify-center shadow-lg`}>
        <span className="text-white text-xs font-bold">{annotation.id?.replace('dmg_', '') || '!'}</span>
      </div>
    </div>
  );
};

// Annotated Image Component - displays an image with damage markers
const AnnotatedImage = ({ image, annotations, label, onMarkerClick, selectedDamage }) => {
  const imageUrl = image?.preview || (image?.url?.startsWith('/uploads/') 
    ? `${process.env.REACT_APP_BACKEND_URL}/api${image.url}` 
    : `${process.env.REACT_APP_BACKEND_URL}${image?.url}`);
  
  const imageAnnotations = annotations?.filter(a => a.image_name === label.toLowerCase()) || [];
  
  return (
    <div className="relative group">
      <div className="aspect-video rounded-lg overflow-hidden border-2 border-slate-600 relative">
        <img 
          src={imageUrl} 
          alt={label}
          className="w-full h-full object-cover"
        />
        {/* Damage markers overlay */}
        {imageAnnotations.map((annotation, idx) => (
          <DamageMarker 
            key={annotation.id || idx}
            annotation={annotation}
            onClick={onMarkerClick}
            isSelected={selectedDamage?.id === annotation.id}
          />
        ))}
        {/* Image label */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
          <p className="text-white text-xs font-medium">{label}</p>
          {imageAnnotations.length > 0 && (
            <p className="text-red-400 text-xs">
              {imageAnnotations.length} damage{imageAnnotations.length > 1 ? 's' : ''} detected
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const NewJob = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [jobId, setJobId] = useState(null);
  
  // Check if we have booking data passed from Bookings page
  const bookingData = location.state?.bookingData;
  
  const [formData, setFormData] = useState({
    source: bookingData ? 'booking' : 'garage_own',
    job_type: bookingData?.requires_pickup ? 'pickup' : 'walk_in',  // walk_in or pickup
    customer_name: bookingData?.customer_name || '',
    customer_mobile: bookingData?.customer_phone || '',
    customer_email: bookingData?.customer_email || '',
    pickup_address: bookingData?.address || '',
    pickup_driver_id: bookingData?.driver_id || ''
  });
  
  const [images, setImages] = useState([]);
  const [aiExtraction, setAiExtraction] = useState(null);
  const [editedData, setEditedData] = useState(bookingData ? {
    vehicle_brand: bookingData.vehicle_brand || '',
    vehicle_model: bookingData.vehicle_model || '',
    vehicle_number: bookingData.vehicle_number || '',
    fuel_type: bookingData.vehicle_fuel_type || '',
    odometer: '',
    damages: []
  } : null);
  const [complaint, setComplaint] = useState(bookingData?.customer_complaint || '');
  const [isReviewing, setIsReviewing] = useState(false);
  const [selectedDamage, setSelectedDamage] = useState(null);
  
  // Booking-specific data
  const [bookingId, setBookingId] = useState(bookingData?.id || null);
  const [bookingInfo, setBookingInfo] = useState(bookingData ? {
    service_category: bookingData.service_category,
    preferred_date: bookingData.preferred_date,
    preferred_time: bookingData.preferred_time,
    address: bookingData.address,
    requires_pickup: bookingData.requires_pickup,
    driver_id: bookingData.driver_id,
    driver_name: bookingData.driver_name
  } : null);

  // Voice input for complaint
  const complaintVoice = useVoiceInput();

  useEffect(() => {
    if (complaintVoice.transcript && !complaintVoice.isListening) {
      setComplaint(prev => prev ? `${prev} ${complaintVoice.transcript}` : complaintVoice.transcript);
      complaintVoice.setTranscript('');
    }
  }, [complaintVoice.transcript, complaintVoice.isListening]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Extract just digits from voice input for mobile
  const handleMobileVoice = (text) => {
    // Convert spoken numbers to digits
    const numberWords = {
      'zero': '0', 'one': '1', 'two': '2', 'three': '3', 'four': '4',
      'five': '5', 'six': '6', 'seven': '7', 'eight': '8', 'nine': '9',
      'oh': '0', 'o': '0'
    };
    
    let processed = text.toLowerCase();
    Object.entries(numberWords).forEach(([word, digit]) => {
      processed = processed.replace(new RegExp(`\\b${word}\\b`, 'g'), digit);
    });
    
    // Extract only digits
    const digits = processed.replace(/\D/g, '').slice(0, 10);
    if (digits) {
      setFormData(prev => ({ ...prev, customer_mobile: digits }));
    }
  };

  // Convert voice input to email format
  const handleEmailVoice = (text) => {
    let processed = text.toLowerCase().trim();
    
    // Common email voice patterns
    const replacements = {
      ' at ': '@',
      ' at the rate ': '@',
      ' at the rate of ': '@',
      'at the rate': '@',
      ' dot ': '.',
      ' period ': '.',
      ' underscore ': '_',
      ' dash ': '-',
      ' hyphen ': '-',
      'gmail dot com': 'gmail.com',
      'yahoo dot com': 'yahoo.com',
      'hotmail dot com': 'hotmail.com',
      'outlook dot com': 'outlook.com',
      'rediffmail dot com': 'rediffmail.com',
      ' ': '' // Remove remaining spaces
    };
    
    Object.entries(replacements).forEach(([pattern, replacement]) => {
      processed = processed.split(pattern).join(replacement);
    });
    
    // Clean up any remaining issues
    processed = processed.replace(/\s+/g, ''); // Remove all spaces
    
    if (processed) {
      setFormData(prev => ({ ...prev, customer_email: processed }));
    }
  };

  const handleCreateJob = async () => {
    if (!formData.customer_name || !formData.customer_mobile || !formData.customer_email) {
      toast.error('Customer name, mobile and email are required');
      return;
    }
    
    if (formData.customer_mobile.length !== 10) {
      toast.error('Mobile number must be 10 digits');
      return;
    }

    // Validate email format
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer_email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    const token = localStorage.getItem('garage_token');
    
    try {
      const cleanedData = {
        source: formData.source,
        job_type: formData.job_type,  // walk_in or pickup
        customer_name: formData.customer_name.trim(),
        customer_mobile: formData.customer_mobile,
        customer_email: formData.customer_email.trim(),
        vehicle_number: editedData?.vehicle_number || null,
        vehicle_make: editedData?.vehicle_brand || null,
        vehicle_model: editedData?.vehicle_model || null,
        vehicle_year: null,
        fuel_type: editedData?.fuel_type || null,
        // Include booking info if creating from booking
        booking_id: bookingId || null,
        service_type: bookingInfo?.service_category || null,
        scheduled_date: bookingInfo?.preferred_date || null,
        scheduled_time: bookingInfo?.preferred_time || null,
        pickup_required: formData.job_type === 'pickup',
        pickup_address: formData.job_type === 'pickup' ? formData.pickup_address : null,
        pickup_driver_id: formData.job_type === 'pickup' ? formData.pickup_driver_id : null
      };
      
      const response = await fetch(`${API_URL}/api/garage/jobs/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanedData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setJobId(data.job.id);
        
        // If creating from booking, update the booking with job_id
        if (bookingId) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            await fetch(`${API_URL}/api/garage/bookings/${bookingId}/link-job?garage_id=${payload.garage_id}&job_id=${data.job.id}`, {
              method: 'PUT',
              headers: { 'Authorization': `Bearer ${token}` }
            });
          } catch (e) {
            console.error('Failed to link booking to job:', e);
          }
        }
        
        toast.success('Job created! Now capture vehicle images.');
        setStep(2);
      } else {
        toast.error(data.detail || 'Failed to create job');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const token = localStorage.getItem('garage_token');
    const imageTypes = ['front', 'rear', 'left', 'right', 'instrument_cluster'];
    
    setLoading(true);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const imageType = imageTypes[images.length + i] || 'damage';
      
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      formDataObj.append('image_type', imageType);
      
      try {
        const response = await fetch(`${API_URL}/api/garage/jobs/${jobId}/images`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataObj
        });
        
        const data = await response.json();
        if (data.success) {
          setImages(prev => [...prev, {
            ...data.image,
            preview: URL.createObjectURL(file),
            type: imageType
          }]);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setLoading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAnalyzeImages = async () => {
    if (images.length < 5) {
      toast.error('Please upload all 5 images (4 sides + dashboard)');
      return;
    }
    
    setAnalyzing(true);
    const token = localStorage.getItem('garage_token');
    
    try {
      const response = await fetch(`${API_URL}/api/garage/jobs/${jobId}/analyze-images`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAiExtraction(data.extraction);
        // Initialize editable data with AI extraction results
        setEditedData({
          vehicle_number: data.extraction.vehicle_number || '',
          make: data.extraction.make || '',
          model: data.extraction.model || '',
          color: data.extraction.color || '',
          odometer_reading: data.extraction.odometer_reading || '',
          fuel_level: data.extraction.fuel_level || '',
          visible_damages: data.extraction.visible_damages || [],
          damage_annotations: data.extraction.damage_annotations || [],
          damage_summary: data.extraction.damage_summary || '',
          notes: data.extraction.notes || ''
        });
        toast.success('AI analysis complete! Review and edit the data below.');
        setStep(3);
        setIsReviewing(true);
      } else {
        toast.error(data.detail || 'Analysis failed');
      }
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveJobCard = async () => {
    if (!complaint.trim()) {
      toast.error('Please enter customer concerns');
      return;
    }
    
    setLoading(true);
    const token = localStorage.getItem('garage_token');
    
    try {
      // First, update vehicle data with edited values
      if (editedData) {
        const updateResponse = await fetch(`${API_URL}/api/garage/jobs/${jobId}/vehicle-data`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            vehicle_number: editedData.vehicle_number,
            vehicle_make: editedData.make,
            vehicle_model: editedData.model,
            vehicle_color: editedData.color,
            odometer_reading: editedData.odometer_reading,
            fuel_level: editedData.fuel_level,
            visible_damages: editedData.visible_damages,
            damage_summary: editedData.damage_summary,
            notes: editedData.notes
          })
        });
        
        const updateData = await updateResponse.json();
        if (!updateData.success) {
          console.warn('Vehicle data update warning:', updateData.detail);
        }
      }
      
      // Then add complaint and finalize
      const response = await fetch(`${API_URL}/api/garage/jobs/${jobId}/complaint`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: complaint })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Job card created successfully!');
        navigate(`/garage/jobs/${jobId}`);
      } else {
        toast.error(data.detail || 'Failed to save job card');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  // Helper to update edited data
  const updateEditedField = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  // Helper to add/remove damage items
  const addDamageItem = () => {
    setEditedData(prev => ({
      ...prev,
      visible_damages: [...(prev.visible_damages || []), '']
    }));
  };

  const updateDamageItem = (index, value) => {
    setEditedData(prev => {
      const damages = [...prev.visible_damages];
      damages[index] = value;
      return { ...prev, visible_damages: damages };
    });
  };

  const removeDamageItem = (index) => {
    setEditedData(prev => ({
      ...prev,
      visible_damages: prev.visible_damages.filter((_, i) => i !== index)
    }));
  };

  const imageLabels = [
    { key: 'front', label: 'Front', icon: '🚗' },
    { key: 'rear', label: 'Rear', icon: '🔙' },
    { key: 'left', label: 'Left Side', icon: '◀️' },
    { key: 'right', label: 'Right Side', icon: '▶️' },
    { key: 'instrument_cluster', label: 'Dashboard', icon: '🔢' }
  ];

  return (
    <div className={`min-h-screen ${theme.pageBg} transition-colors duration-200`}>
      {/* Progress Steps */}
      <div className="max-w-2xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {['Customer', 'Photos', 'AI Analysis'].map((label, idx) => (
            <div key={label} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step > idx + 1 ? 'bg-green-500 text-white' :
                step === idx + 1 ? 'bg-orange-500 text-white' : `${theme.cardBgSolid} ${theme.textSecondary}`
              }`}>
                {step > idx + 1 ? <Check className="w-4 h-4" /> : idx + 1}
              </div>
              <span className={`ml-2 text-sm ${step === idx + 1 ? theme.textPrimary : theme.textMuted}`}>
                {label}
              </span>
              {idx < 2 && <div className={`w-12 h-0.5 mx-2 ${step > idx + 1 ? 'bg-green-500' : theme.isDarkMode ? 'bg-slate-700' : 'bg-gray-300'}`} />}
            </div>
          ))}
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 pb-6">
        {/* Step 1: Customer Info with Voice Input */}
        {step === 1 && (
          <Card className={`${theme.cardBg} border`}>
            <CardHeader>
              <CardTitle className={`${theme.textPrimary} flex items-center gap-2`}>
                <User className="w-5 h-5" /> Customer Details
              </CardTitle>
              <CardDescription className={theme.textSecondary}>
                Use voice input <Mic className="w-3 h-3 inline" /> to fill details hands-free
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Booking Info Banner - shown when creating job from booking */}
              {bookingData && (
                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarDays className="w-5 h-5 text-blue-400" />
                    <span className="font-semibold text-blue-400">Creating Job from Booking</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-slate-300">
                    <p><span className="text-slate-500">Service:</span> {bookingInfo?.service_category}</p>
                    <p><span className="text-slate-500">Date:</span> {bookingInfo?.preferred_date}</p>
                    <p><span className="text-slate-500">Time:</span> {bookingInfo?.preferred_time}</p>
                    {bookingInfo?.requires_pickup && (
                      <p><span className="text-slate-500">Pickup:</span> {bookingInfo?.driver_name || 'Required'}</p>
                    )}
                  </div>
                  {bookingInfo?.address && (
                    <p className="text-sm text-slate-400 mt-2">
                      <MapPin className="w-3 h-3 inline mr-1" />
                      {bookingInfo.address}
                    </p>
                  )}
                </div>
              )}
              
              {/* Source Selection */}
              <div>
                <Label className="text-slate-300 mb-2 block">Job Source</Label>
                <RadioGroup 
                  value={formData.source} 
                  onValueChange={(v) => handleInputChange('source', v)}
                  className="flex gap-4"
                  disabled={!!bookingData}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="garage_own" id="own" className="border-orange-500 text-orange-500" />
                    <Label htmlFor="own" className="text-slate-300 cursor-pointer">Own Order</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="propitstop" id="propitstop" className="border-orange-500 text-orange-500" />
                    <Label htmlFor="propitstop" className="text-slate-300 cursor-pointer">Propitstop Order</Label>
                  </div>
                  {bookingData && (
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="booking" id="booking" className="border-blue-500 text-blue-500" />
                      <Label htmlFor="booking" className="text-blue-400 cursor-pointer">From Booking</Label>
                    </div>
                  )}
                </RadioGroup>
              </div>

              {/* Job Type Selection - Walk-in or Pickup */}
              <div>
                <Label className="text-slate-300 mb-2 block">Job Type *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleInputChange('job_type', 'walk_in')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.job_type === 'walk_in' 
                        ? 'border-green-500 bg-green-500/10' 
                        : 'border-slate-600 hover:border-green-500/50'
                    }`}
                    data-testid="job-type-walkin"
                  >
                    <Car className="w-8 h-8 mx-auto mb-2 text-green-400" />
                    <p className="font-medium text-white">Walk-in</p>
                    <p className="text-xs text-slate-400 mt-1">Vehicle at garage</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('job_type', 'pickup')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.job_type === 'pickup' 
                        ? 'border-blue-500 bg-blue-500/10' 
                        : 'border-slate-600 hover:border-blue-500/50'
                    }`}
                    data-testid="job-type-pickup"
                  >
                    <MapPin className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                    <p className="font-medium text-white">Pickup</p>
                    <p className="text-xs text-slate-400 mt-1">Need to pick up</p>
                  </button>
                </div>
              </div>

              {/* Customer Name with Voice */}
              <div>
                <Label className="text-slate-300">Customer Name *</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      value={formData.customer_name}
                      onChange={(e) => handleInputChange('customer_name', e.target.value)}
                      className="pl-10 bg-slate-700/50 border-slate-600 text-white"
                      placeholder="Speak or type customer name"
                      data-testid="customer-name"
                    />
                  </div>
                  <VoiceInputButton 
                    onResult={(text) => handleInputChange('customer_name', text)}
                    placeholder="Speak customer name"
                  />
                </div>
              </div>

              {/* Mobile with Voice */}
              <div>
                <Label className="text-slate-300">Mobile Number *</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      value={formData.customer_mobile}
                      onChange={(e) => handleInputChange('customer_mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="pl-10 bg-slate-700/50 border-slate-600 text-white text-lg tracking-wider"
                      placeholder="9876543210"
                      maxLength={10}
                      data-testid="customer-mobile"
                    />
                  </div>
                  <VoiceInputButton 
                    onResult={handleMobileVoice}
                    placeholder="Speak mobile number"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Say digits like "nine eight seven six..."
                </p>
              </div>

              {/* Customer Email with Voice */}
              <div>
                <Label className="text-slate-300">Email Address *</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => handleInputChange('customer_email', e.target.value)}
                      className="pl-10 bg-slate-700/50 border-slate-600 text-white"
                      placeholder="customer@email.com"
                      data-testid="customer-email"
                    />
                  </div>
                  <VoiceInputButton 
                    onResult={handleEmailVoice}
                    placeholder="Speak email address"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Say like "john at gmail dot com" • Required to send estimate
                </p>
              </div>

              <Button 
                onClick={handleCreateJob}
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 h-12 text-lg"
                disabled={loading}
                data-testid="create-job-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Camera className="w-5 h-5 mr-2" />}
                Continue to Photo Capture
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Image Capture - All 4 Sides + Dashboard */}
        {step === 2 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Camera className="w-5 h-5" /> Capture Vehicle Images
              </CardTitle>
              <CardDescription>
                Take photos of all 4 sides + instrument cluster for AI analysis
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Image Grid - Required Photos - CLICKABLE */}
              <div className="space-y-3">
                {imageLabels.map((imgLabel, idx) => {
                  const uploadedImg = images.find(img => img.type === imgLabel.key) || images[idx];
                  const isUploaded = !!uploadedImg;
                  
                  return (
                    <div 
                      key={imgLabel.key}
                      onClick={() => {
                        if (!isUploaded && fileInputRef.current) {
                          fileInputRef.current.click();
                        }
                      }}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${
                        isUploaded 
                          ? 'border-green-500/50 bg-green-500/10' 
                          : 'border-dashed border-slate-600 hover:border-orange-500/50 hover:bg-slate-700/30'
                      }`}
                    >
                      {isUploaded ? (
                        <img 
                          src={uploadedImg.preview} 
                          alt={imgLabel.label}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-slate-700 flex items-center justify-center text-2xl">
                          {imgLabel.icon}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className={`font-medium ${isUploaded ? 'text-green-400' : 'text-white'}`}>
                          {imgLabel.label}
                        </p>
                        <p className="text-xs text-slate-400">
                          {isUploaded 
                            ? 'Uploaded ✓' 
                            : imgLabel.key === 'instrument_cluster' 
                              ? 'Tap to capture odometer' 
                              : 'Tap to capture photo'}
                        </p>
                      </div>
                      {isUploaded ? (
                        <Check className="w-6 h-6 text-green-500" />
                      ) : (
                        <div className="px-3 py-1 bg-orange-500/20 rounded-full">
                          <span className="text-orange-400 text-sm font-medium">Upload</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Upload Progress */}
              <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                <span className="text-slate-300">Photos uploaded</span>
                <span className={`font-bold ${images.length >= 5 ? 'text-green-400' : 'text-orange-400'}`}>
                  {images.length} / 5
                </span>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageUpload}
                data-testid="image-input"
              />

              {/* Upload Button - More visible */}
              <Button 
                type="button"
                onClick={() => {
                  console.log('Upload button clicked, fileInputRef:', fileInputRef.current);
                  if (fileInputRef.current) {
                    fileInputRef.current.click();
                  }
                }}
                className="w-full h-14 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-lg"
                disabled={loading}
              >
                <Upload className="w-6 h-6 mr-2" />
                {loading ? 'Uploading...' : 'Select Images to Upload'}
              </Button>

              <Button 
                onClick={handleAnalyzeImages}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 h-12 text-lg"
                disabled={analyzing || images.length === 0}
                data-testid="analyze-btn"
              >
                {analyzing ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-5 h-5 mr-2" />
                )}
                {analyzing ? 'AI Analyzing Images...' : `Analyze ${images.length} Image${images.length !== 1 ? 's' : ''} with AI`}
              </Button>

              {images.length === 0 && (
                <p className="text-center text-yellow-500 text-sm flex items-center justify-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Upload at least 1 photo to enable AI analysis
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Review & Edit AI Results */}
        {step === 3 && editedData && (
          <div className="space-y-4">
            {/* Review Header */}
            <Card className="bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border-orange-500/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Edit3 className="w-6 h-6 text-orange-400" />
                  <div>
                    <p className="text-white font-medium">Review & Edit Vehicle Data</p>
                    <p className="text-sm text-slate-400">AI has analyzed the images. Please review and correct any data before saving.</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  AI Confidence: {Math.round((aiExtraction?.confidence_score || 0) * 100)}%
                </p>
              </CardContent>
            </Card>

            {/* Annotated Images with Damage Markers */}
            {images.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Camera className="w-5 h-5 text-purple-400" /> Vehicle Images with Damage Detection
                  </CardTitle>
                  <CardDescription>
                    {editedData.damage_annotations?.length > 0 
                      ? `${editedData.damage_annotations.length} damage(s) detected - click markers for details`
                      : 'No damages detected in images'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {['Front', 'Rear', 'Left', 'Right', 'Dashboard'].map((label, idx) => {
                      const image = images[idx];
                      if (!image) return null;
                      return (
                        <AnnotatedImage
                          key={idx}
                          image={image}
                          annotations={editedData.damage_annotations}
                          label={label}
                          onMarkerClick={(annotation) => setSelectedDamage(annotation)}
                          selectedDamage={selectedDamage}
                        />
                      );
                    })}
                  </div>
                  
                  {/* Selected Damage Details */}
                  {selectedDamage && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-red-400 font-medium flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            Damage #{selectedDamage.id?.replace('dmg_', '') || '?'}: {selectedDamage.damage_type}
                          </p>
                          <p className="text-white text-sm mt-1">{selectedDamage.description}</p>
                          <p className="text-slate-400 text-xs mt-1">
                            Location: {selectedDamage.image_name} view | Severity: 
                            <span className={`ml-1 ${
                              selectedDamage.severity === 'severe' ? 'text-red-400' :
                              selectedDamage.severity === 'moderate' ? 'text-orange-400' : 'text-yellow-400'
                            }`}>
                              {selectedDamage.severity}
                            </span>
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedDamage(null)}
                          className="text-slate-400 hover:text-white"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Editable Vehicle Info */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2">
                  <Car className="w-5 h-5 text-blue-400" /> Vehicle Information
                </CardTitle>
                <CardDescription>Edit any incorrect data detected by AI</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {/* Registration Number */}
                  <div className="col-span-2">
                    <Label className="text-slate-400 text-xs mb-1 block">Registration Number</Label>
                    <Input
                      value={editedData.vehicle_number}
                      onChange={(e) => updateEditedField('vehicle_number', e.target.value.toUpperCase())}
                      placeholder="KA01AB1234"
                      className="bg-slate-700/50 border-slate-600 text-white font-bold text-lg"
                      data-testid="edit-vehicle-number"
                    />
                  </div>

                  {/* Make */}
                  <div>
                    <Label className="text-slate-400 text-xs mb-1 block">Make</Label>
                    <Input
                      value={editedData.make}
                      onChange={(e) => updateEditedField('make', e.target.value)}
                      placeholder="e.g., Maruti, Honda"
                      className="bg-slate-700/50 border-slate-600 text-white"
                      data-testid="edit-make"
                    />
                  </div>

                  {/* Model */}
                  <div>
                    <Label className="text-slate-400 text-xs mb-1 block">Model</Label>
                    <Input
                      value={editedData.model}
                      onChange={(e) => updateEditedField('model', e.target.value)}
                      placeholder="e.g., Swift, City"
                      className="bg-slate-700/50 border-slate-600 text-white"
                      data-testid="edit-model"
                    />
                  </div>

                  {/* Color */}
                  <div>
                    <Label className="text-slate-400 text-xs mb-1 block">Color</Label>
                    <Input
                      value={editedData.color}
                      onChange={(e) => updateEditedField('color', e.target.value)}
                      placeholder="e.g., White, Silver"
                      className="bg-slate-700/50 border-slate-600 text-white"
                      data-testid="edit-color"
                    />
                  </div>

                  {/* Odometer */}
                  <div>
                    <Label className="text-slate-400 text-xs mb-1 flex items-center gap-1">
                      <Gauge className="w-3 h-3" /> Odometer Reading
                    </Label>
                    <Input
                      value={editedData.odometer_reading}
                      onChange={(e) => updateEditedField('odometer_reading', e.target.value)}
                      placeholder="e.g., 45000 km"
                      className="bg-slate-700/50 border-slate-600 text-white"
                      data-testid="edit-odometer"
                    />
                  </div>

                  {/* Fuel Level */}
                  <div className="col-span-2">
                    <Label className="text-slate-400 text-xs mb-1 flex items-center gap-1">
                      <Fuel className="w-3 h-3" /> Fuel Level
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={editedData.fuel_level}
                        onChange={(e) => updateEditedField('fuel_level', e.target.value)}
                        placeholder="e.g., 50%, Half, 3/4"
                        className="bg-slate-700/50 border-slate-600 text-white flex-1"
                        data-testid="edit-fuel-level"
                      />
                      {/* Quick select buttons */}
                      <div className="flex gap-1">
                        {['Empty', '1/4', '1/2', '3/4', 'Full'].map((level) => (
                          <Button
                            key={level}
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => updateEditedField('fuel_level', level)}
                            className={`text-xs px-2 ${
                              editedData.fuel_level === level 
                                ? 'bg-orange-500 border-orange-500 text-white' 
                                : 'border-slate-600 text-slate-400 hover:bg-slate-700'
                            }`}
                          >
                            {level}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Editable Damage List */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" /> Visible Damages
                </CardTitle>
                <CardDescription>Add, edit or remove detected damages</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {editedData.visible_damages?.length > 0 ? (
                  editedData.visible_damages.map((damage, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        value={damage}
                        onChange={(e) => updateDamageItem(idx, e.target.value)}
                        placeholder="Describe damage location and type..."
                        className="bg-slate-700/50 border-slate-600 text-white flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeDamageItem(idx)}
                        className="text-red-400 hover:bg-red-500/20"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-green-400 font-medium flex items-center gap-2">
                      <Check className="w-4 h-4" /> No visible damage detected
                    </p>
                  </div>
                )}
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={addDamageItem}
                  className="w-full border-dashed border-slate-600 text-slate-400 hover:bg-slate-700"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Damage Entry
                </Button>
              </CardContent>
            </Card>

            {/* Customer Concerns with Voice Input */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2">
                  <Mic className="w-5 h-5 text-orange-500" /> Customer Concerns
                </CardTitle>
                <CardDescription>
                  Use voice to capture what the customer reported
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative">
                  <Textarea
                    value={complaint}
                    onChange={(e) => setComplaint(e.target.value)}
                    placeholder="Tap the mic button and speak, or type here..."
                    className="min-h-[120px] bg-slate-700/50 border-slate-600 text-white pr-12"
                    data-testid="complaint-input"
                  />
                  <Button
                    type="button"
                    variant={complaintVoice.isListening ? "destructive" : "ghost"}
                    size="icon"
                    onClick={complaintVoice.isListening ? complaintVoice.stopListening : complaintVoice.startListening}
                    className={`absolute right-2 top-2 ${
                      complaintVoice.isListening 
                        ? 'animate-pulse bg-red-500' 
                        : 'text-orange-500 hover:bg-orange-500/10'
                    }`}
                  >
                    {complaintVoice.isListening ? <Square className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </Button>
                </div>

                {complaintVoice.isListening && (
                  <div className="flex items-center gap-2 p-2 bg-red-500/10 rounded-lg">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-red-400 text-sm">Listening... speak now</span>
                    {complaintVoice.transcript && (
                      <span className="text-slate-400 text-sm">"{complaintVoice.transcript}"</span>
                    )}
                  </div>
                )}

                <p className="text-xs text-slate-500">
                  Example: "Car making noise when braking, AC not cooling, engine vibration at high speed"
                </p>
                
                <Button 
                  onClick={handleSaveJobCard}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 h-14 text-lg"
                  disabled={loading || !complaint.trim()}
                  data-testid="save-job-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5 mr-2" />
                  )}
                  Review Complete - Save Job Card
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default NewJob;
